import { useAsync, type AsyncState } from "@/components/useAsync";
import {
  fetchLocationQuestionnaires,
  fetchPrivateResponse,
  fetchWorkingComment,
  type LocationQuestionnaire,
} from "@/data/interventionsRepository";
import type { QuestionnaireResponse } from "@/domain/questionnaire";
import { fetchInterventionFeedback, type InterventionFeedback } from "@/data/interventionFeedbackRepository";

export interface EditorSources {
  locations: LocationQuestionnaire[];
  response: QuestionnaireResponse | null;
  /** Administrators' working comment; "" when there is none or it isn't loaded. */
  comment: string;
  feedback: InterventionFeedback[];
}

/**
 * Loads the location→questionnaire assignments and, when editing, the saved
 * response and (for administrators) the working comment.
 */
export function useEditorSources(
  interventionId: string | null,
  loadComment: boolean,
  loadFeedback = false,
): AsyncState<EditorSources> {
  const [state] = useAsync(
    `editor:${interventionId ?? "new"}:${loadComment ? "comment" : ""}:${loadFeedback ? "feedback" : ""}`,
    async () => {
      const [locations, response, comment, feedback] = await Promise.all([
        fetchLocationQuestionnaires(),
        interventionId ? fetchPrivateResponse(interventionId) : Promise.resolve(null),
        interventionId && loadComment ? fetchWorkingComment(interventionId) : Promise.resolve(""),
        interventionId && loadFeedback ? fetchInterventionFeedback(interventionId) : Promise.resolve([]),
      ]);
      return { locations, response, comment, feedback };
    },
  );
  return state;
}
